import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING, TOTAL_SLIDES } from "./constants";

/* ───────────────────────── helpers ───────────────────────── */

/** Two-digit zero-pad without relying on String.prototype.padStart typings. */
export function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** 0..1 opacity → 2-digit hex alpha suffix. */
export function hexA(o: number): string {
  const v = Math.max(0, Math.min(255, Math.round(o * 255)));
  const s = v.toString(16);
  return s.length < 2 ? `0${s}` : s;
}

export function glow(c: string, strong = false): string {
  return strong
    ? `0 0 26px ${c}cc, 0 0 60px ${c}77, 0 0 120px ${c}33`
    : `0 0 16px ${c}99, 0 0 40px ${c}44`;
}

/** Spring-eased 0→1 appear value. */
export function spAppear(frame: number, fps: number, delay = 0): number {
  return interpolate(
    spring({ frame: frame - delay, fps, config: SPRING.snappy }),
    [0, 1],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );
}

/** Fade the last 24 frames of a scene out for a clean cut. */
export function fadeOut(frame: number, durationInFrames: number): number {
  return interpolate(
    frame,
    [durationInFrames - 24, durationInFrames],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
  );
}

/* ───────────────────────── backdrop ───────────────────────── */

export const Backdrop: React.FC<{
  frame: number;
  accent: string;
  dark?: boolean;
}> = ({ frame, accent, dark = false }) => {
  const drift = Math.sin(frame * 0.005) * 8;
  const orbs = [
    { x: 240, y: 250, s: 580, c: accent, sp: 0.005, ph: 0 },
    { x: 1680, y: 330, s: 500, c: COLORS.neonPurple, sp: 0.004, ph: 2 },
    { x: 960, y: 880, s: 660, c: COLORS.neonBlue, sp: 0.003, ph: 4 },
    { x: 430, y: 820, s: 400, c: COLORS.neonPink, sp: 0.0035, ph: 1.2 },
    { x: 1520, y: 780, s: 440, c: COLORS.neonCyan, sp: 0.0045, ph: 3.1 },
  ];
  const particleColors = [
    COLORS.neonPink,
    COLORS.neonBlue,
    COLORS.neonGreen,
    COLORS.neonCyan,
    COLORS.neonPurple,
    COLORS.neonYellow,
    COLORS.neonOrange,
  ];
  return (
    <>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 80% 60% at 50% 44%, ${accent}${
            dark ? "10" : "1c"
          } 0%, transparent 70%)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `linear-gradient(${accent}12 1px, transparent 1px), linear-gradient(90deg, ${accent}12 1px, transparent 1px)`,
          backgroundSize: "82px 82px",
          transform: `perspective(900px) rotateX(34deg) translateY(${drift}px) scale(1.6)`,
          transformOrigin: "50% 100%",
          maskImage: "linear-gradient(to bottom, transparent 0%, #000 38%, #000 100%)",
        }}
      />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {orbs.map((o, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: o.x + Math.sin(frame * o.sp + o.ph) * 44 - o.s / 2,
              top: o.y + Math.cos(frame * o.sp * 0.7 + o.ph) * 30 - o.s / 2,
              width: o.s,
              height: o.s,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${o.c}0e 0%, transparent 70%)`,
            }}
          />
        ))}
      </div>
      {[...Array(22)].map((_, i) => {
        const px = 90 + ((i * 167) % 1820);
        const py = 50 + ((i * 101) % 980);
        const c = particleColors[i % particleColors.length];
        const sz = 3 + (i % 4);
        return (
          <div
            key={`p-${i}`}
            style={{
              position: "absolute",
              left: px + Math.sin(frame * 0.02 + i * 1.3) * 26,
              top: py + Math.cos(frame * 0.015 + i * 1.7) * 20,
              width: sz,
              height: sz,
              borderRadius: "50%",
              background: c,
              boxShadow: `0 0 ${sz * 3}px ${c}cc, 0 0 ${sz * 6}px ${c}55`,
              opacity: 0.55 + Math.sin(frame * 0.03 + i) * 0.3,
              pointerEvents: "none",
            }}
          />
        );
      })}
    </>
  );
};

/* ───────────────────────── scene shell ───────────────────────── */

export const SceneShell: React.FC<{
  accent: string;
  dark?: boolean;
  children: React.ReactNode;
}> = ({ accent, dark, children }) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: fadeOut(frame, durationInFrames),
        overflow: "hidden",
      }}
    >
      <Backdrop frame={frame} accent={accent} dark={dark} />
      {children}
    </AbsoluteFill>
  );
};

/* ───────────────────────── slide chrome ───────────────────────── */

export const SlideChip: React.FC<{
  n: number;
  color: string;
  appear: number;
  label?: string;
}> = ({ n, color, appear, label }) => (
  <div
    style={{
      position: "absolute",
      top: 44,
      left: 62,
      opacity: appear,
      display: "flex",
      gap: 12,
      alignItems: "center",
      zIndex: 30,
    }}
  >
    <div
      style={{
        padding: "10px 20px",
        borderRadius: 30,
        background: `${color}1c`,
        border: `1.5px solid ${color}aa`,
        fontSize: 18,
        fontFamily: FONTS.mono,
        color,
        fontWeight: 800,
        letterSpacing: 2.5,
        textShadow: glow(color),
      }}
    >
      {pad2(n)} / {pad2(TOTAL_SLIDES)}
    </div>
    {label && (
      <div
        style={{
          fontSize: 15,
          fontFamily: FONTS.mono,
          color: COLORS.textMuted,
          letterSpacing: 2,
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
    )}
  </div>
);

export const SlideTitle: React.FC<{
  text: React.ReactNode;
  subtitle?: React.ReactNode;
  color: string;
  appear: number;
  size?: number;
}> = ({ text, subtitle, color, appear, size = 70 }) => (
  <div
    style={{
      position: "absolute",
      top: 104,
      left: 0,
      right: 0,
      textAlign: "center",
      opacity: appear,
      transform: `translateY(${(1 - appear) * -30}px)`,
      zIndex: 20,
    }}
  >
    <div
      style={{
        fontSize: size,
        fontFamily: FONTS.heading,
        fontWeight: 900,
        color: COLORS.textPrimary,
        textShadow: `0 0 40px ${color}dd, 0 0 80px ${color}55`,
        letterSpacing: -1.5,
        lineHeight: 1.08,
      }}
    >
      {text}
    </div>
    {subtitle && (
      <div
        style={{
          fontSize: 27,
          fontFamily: FONTS.body,
          color,
          marginTop: 12,
          opacity: Math.max(0, (appear - 0.3) / 0.7),
          letterSpacing: 0.6,
          fontWeight: 600,
          textShadow: glow(color),
        }}
      >
        {subtitle}
      </div>
    )}
  </div>
);

/* ───────────────────────── 3D card ───────────────────────── */

export const Card3D: React.FC<{
  x?: number;
  y?: number;
  w: number;
  h: number;
  color: string;
  frame: number;
  delay: number;
  fps: number;
  rotY?: number;
  inline?: boolean;
  children: React.ReactNode;
}> = ({
  x = 0,
  y = 0,
  w,
  h,
  color,
  frame,
  delay,
  fps,
  rotY = 0,
  inline = false,
  children,
}) => {
  const sp = spring({ frame: frame - delay, fps, config: SPRING.bouncy });
  const op = interpolate(sp, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const sc = interpolate(sp, [0, 1], [0.55, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const floatY = Math.sin(frame * 0.024 + delay * 0.05) * 7;
  const wobble = Math.sin(frame * 0.01 + delay) * 2;

  const base: React.CSSProperties = inline
    ? {
        position: "relative",
        width: w,
        height: h,
        flexShrink: 0,
        opacity: op,
        transform: `translateY(${floatY}px) scale(${sc}) perspective(1300px) rotateY(${
          rotY + wobble
        }deg)`,
        transformStyle: "preserve-3d",
      }
    : {
        position: "absolute",
        left: x,
        top: y + floatY,
        width: w,
        height: h,
        opacity: op,
        transform: `scale(${sc}) perspective(1300px) rotateY(${rotY + wobble}deg)`,
        transformStyle: "preserve-3d",
      };

  return (
    <div style={base}>
      <div
        style={{
          width: "100%",
          height: "100%",
          background: `linear-gradient(140deg, ${color}26, ${COLORS.bgCard}f2)`,
          border: `2.5px solid ${color}`,
          borderRadius: 22,
          padding: 28,
          boxShadow: `0 0 42px ${color}55, 0 0 90px ${color}22, 0 24px 46px rgba(0,0,0,0.55)`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ───────────────────────── punchline ───────────────────────── */

export const Punchline: React.FC<{
  text: React.ReactNode;
  color: string;
  appear: number;
}> = ({ text, color, appear }) => (
  <div
    style={{
      position: "absolute",
      bottom: 64,
      left: 0,
      right: 0,
      textAlign: "center",
      opacity: appear,
      transform: `translateY(${(1 - appear) * 22}px)`,
      zIndex: 25,
    }}
  >
    <div
      style={{
        display: "inline-block",
        fontSize: 36,
        fontFamily: FONTS.heading,
        fontWeight: 800,
        color: COLORS.textPrimary,
        padding: "16px 40px",
        borderRadius: 18,
        background: `${COLORS.bgCard}e6`,
        border: `2px solid ${color}`,
        textShadow: glow(color, true),
        letterSpacing: -0.3,
      }}
    >
      {text}
    </div>
  </div>
);

/* ───────────────────────── teaching callouts ───────────────────────── */

/** Boxed precise definition of a term. */
export const DefBox: React.FC<{
  term: string;
  body: React.ReactNode;
  color: string;
  appear: number;
  style?: React.CSSProperties;
  width?: number;
}> = ({ term, body, color, appear, style, width = 640 }) => (
  <div
    style={{
      width,
      opacity: appear,
      transform: `translateY(${(1 - appear) * 26}px) perspective(1200px) rotateX(${
        (1 - appear) * 10
      }deg)`,
      background: `linear-gradient(135deg, ${color}1e, ${COLORS.bgCard}f2)`,
      border: `2px solid ${color}`,
      borderRadius: 18,
      padding: "20px 26px",
      boxShadow: `0 0 34px ${color}44, 0 18px 38px rgba(0,0,0,0.5)`,
      ...style,
    }}
  >
    <div
      style={{
        fontSize: 15,
        fontFamily: FONTS.mono,
        fontWeight: 800,
        letterSpacing: 3,
        color,
        textShadow: glow(color),
        marginBottom: 8,
      }}
    >
      ✦ DEFINITION
    </div>
    <div
      style={{
        fontSize: 25,
        fontFamily: FONTS.heading,
        fontWeight: 800,
        color: COLORS.textPrimary,
        marginBottom: 6,
      }}
    >
      {term}
    </div>
    <div
      style={{
        fontSize: 20,
        fontFamily: FONTS.body,
        color: COLORS.textSecondary,
        lineHeight: 1.4,
      }}
    >
      {body}
    </div>
  </div>
);

/** The same idea, restated in plain language with a tiny analogy. */
export const PlainBox: React.FC<{
  body: React.ReactNode;
  appear: number;
  style?: React.CSSProperties;
  width?: number;
  color?: string;
}> = ({ body, appear, style, width = 640, color = COLORS.neonAmber }) => (
  <div
    style={{
      width,
      opacity: appear,
      transform: `translateY(${(1 - appear) * 26}px) perspective(1200px) rotateX(${
        (1 - appear) * -10
      }deg)`,
      background: `linear-gradient(135deg, ${color}1c, ${COLORS.bgCard}f0)`,
      border: `2px dashed ${color}`,
      borderRadius: 18,
      padding: "20px 26px",
      boxShadow: `0 0 30px ${color}33, 0 18px 38px rgba(0,0,0,0.5)`,
      ...style,
    }}
  >
    <div
      style={{
        fontSize: 15,
        fontFamily: FONTS.mono,
        fontWeight: 800,
        letterSpacing: 3,
        color,
        textShadow: glow(color),
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span style={{ fontSize: 19 }}>💡</span> IN PLAIN WORDS
    </div>
    <div
      style={{
        fontSize: 21,
        fontFamily: FONTS.body,
        color: COLORS.textPrimary,
        lineHeight: 1.42,
        fontWeight: 500,
      }}
    >
      {body}
    </div>
  </div>
);

/* ───────────────────────── small pill ───────────────────────── */

export const Pill: React.FC<{
  text: React.ReactNode;
  color: string;
  appear?: number;
  solid?: boolean;
  size?: number;
}> = ({ text, color, appear = 1, solid = false, size = 18 }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      padding: `${size * 0.42}px ${size * 0.95}px`,
      borderRadius: 999,
      fontSize: size,
      fontFamily: FONTS.mono,
      fontWeight: 800,
      letterSpacing: 0.6,
      color: solid ? COLORS.bg : color,
      background: solid ? color : `${color}1e`,
      border: `1.5px solid ${color}`,
      boxShadow: `0 0 18px ${color}55`,
      opacity: appear,
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </div>
);

/* ───────────────────────── brand logo ───────────────────────── */

/**
 * Renders a brand logo from public/assets/brands/<name>.svg.
 * Replace the SVG in that folder with an official asset (same filename)
 * and it updates everywhere automatically.
 */
export const BrandLogo: React.FC<{
  name: string;
  size: number;
  style?: React.CSSProperties;
}> = ({ name, size, style }) => (
  <Img
    src={staticFile(`assets/brands/${name}.svg`)}
    style={{
      width: size,
      height: size,
      objectFit: "contain",
      display: "block",
      ...style,
    }}
  />
);

/* ───────────────────────── snapshot placeholder ───────────────────────── */

/**
 * A browser-chrome frame for a product screenshot.
 * Pass `src` (e.g. staticFile("assets/snapshots/chat-nudge.png")) to show a real
 * screenshot; omit it to show the "drop screenshot here" placeholder.
 */
export const SnapshotFrame: React.FC<{
  w: number;
  h: number;
  color: string;
  frame: number;
  delay: number;
  fps: number;
  label: string;
  url?: string;
  rotY?: number;
  inline?: boolean;
  x?: number;
  y?: number;
  src?: string;
}> = ({
  w,
  h,
  color,
  frame,
  delay,
  fps,
  label,
  url = "eakc.app",
  rotY = 0,
  inline = false,
  x = 0,
  y = 0,
  src,
}) => {
  const sp = spring({ frame: frame - delay, fps, config: SPRING.gentle });
  const op = interpolate(sp, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const sc = interpolate(sp, [0, 1], [0.7, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const floatY = Math.sin(frame * 0.02 + delay * 0.06) * 6;
  const dash = -(frame * 0.6) % 22;

  const base: React.CSSProperties = inline
    ? {
        position: "relative",
        width: w,
        height: h,
        flexShrink: 0,
      }
    : { position: "absolute", left: x, top: y, width: w, height: h };

  return (
    <div
      style={{
        ...base,
        opacity: op,
        transform: `translateY(${floatY}px) scale(${sc}) perspective(1400px) rotateY(${rotY}deg)`,
        transformStyle: "preserve-3d",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 16,
          overflow: "hidden",
          background: COLORS.bgCardLight,
          border: `2px solid ${color}`,
          boxShadow: `0 0 40px ${color}44, 0 26px 50px rgba(0,0,0,0.6)`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* browser top bar */}
        <div
          style={{
            height: 38,
            background: COLORS.bgCard,
            borderBottom: `1.5px solid ${color}55`,
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {[COLORS.neonRed, COLORS.neonYellow, COLORS.neonGreen].map((c) => (
            <div
              key={c}
              style={{
                width: 11,
                height: 11,
                borderRadius: "50%",
                background: c,
                boxShadow: `0 0 8px ${c}`,
              }}
            />
          ))}
          <div
            style={{
              marginLeft: 12,
              flex: 1,
              height: 20,
              borderRadius: 6,
              background: COLORS.bg,
              border: `1px solid ${color}33`,
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              fontSize: 12,
              fontFamily: FONTS.mono,
              color: COLORS.textMuted,
            }}
          >
            {url}
          </div>
        </div>
        {/* content — real screenshot if provided, else placeholder */}
        {src ? (
          <Img
            src={src}
            style={{ flex: 1, width: "100%", minHeight: 0, objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              flex: 1,
              margin: 16,
              borderRadius: 12,
              border: `2.5px dashed ${color}99`,
              background: `repeating-linear-gradient(45deg, ${color}08 0 14px, transparent 14px 28px)`,
              backgroundPositionX: dash,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: Math.min(46, h * 0.16),
                filter: `drop-shadow(0 0 14px ${color})`,
              }}
            >
              📸
            </div>
            <div
              style={{
                fontSize: Math.min(20, w * 0.052),
                fontFamily: FONTS.heading,
                fontWeight: 800,
                color,
                textAlign: "center",
                textShadow: glow(color),
                padding: "0 14px",
              }}
            >
              {label}
            </div>
            <div
              style={{
                fontSize: Math.min(13, w * 0.034),
                fontFamily: FONTS.mono,
                color: COLORS.textMuted,
                letterSpacing: 1,
              }}
            >
              SNAPSHOT PLACEHOLDER · drop screenshot here
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
